import { ApiError } from '../../api/errors';
import { formatNumber, NBSP } from '../../lib/format';

/*
 * Надсилання файлів до API тим самим сеансом (cookie, заголовок X-ERS-Client: web):
 * — uploadBinary: двійкове тіло (аудіо чи відео для розшифровки) з поступом завантаження;
 *   без ключа ідемпотентності (сервер приймає потік, повтор — нова спроба);
 * — readFileText: вміст текстового файла (CSV, JSON) для операцій, що приймають його в JSON-тілі.
 * Помилки сервера — ApiError з поясненням українською (problem+json), як у решті клієнта.
 */

export type UploadProgress = { loaded: number; total: number; fraction: number };

/** Розмір файла людською мовою: «812 Б», «2,3 КБ», «14,6 МБ», «1,2 ГБ». */
export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) return '';
  if (bytes < 1024) return `${formatNumber(bytes)}${NBSP}Б`;
  const units = ['КБ', 'МБ', 'ГБ', 'ТБ'];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${formatNumber(v, { maximumFractionDigits: v < 10 ? 1 : 0 })}${NBSP}${units[i]}`;
}

function parseBody(text: string): unknown {
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

/**
 * POST двійкового файла. `path` — повна адреса операції з параметрами запиту
 * (напр. `/api/v1/meetings/{id}/uploads?fileName=…&consentConfirmed=true`).
 * Тип вмісту — тип файла або application/octet-stream.
 */
export function uploadBinary<T>(
  path: string,
  file: Blob,
  opts: { contentType?: string; onProgress?: (p: UploadProgress) => void; signal?: AbortSignal } = {},
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', path, true);
    xhr.withCredentials = true;
    xhr.setRequestHeader('x-ers-client', 'web');
    xhr.setRequestHeader('accept', 'application/json');
    xhr.setRequestHeader('content-type', opts.contentType || file.type || 'application/octet-stream');
    xhr.upload.onprogress = (e) => {
      if (!opts.onProgress) return;
      const total = e.lengthComputable ? e.total : file.size;
      opts.onProgress({ loaded: e.loaded, total, fraction: total ? Math.min(1, e.loaded / total) : 0 });
    };
    xhr.onload = () => {
      const body = parseBody(xhr.responseText);
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(body as T);
        return;
      }
      reject(ApiError.fromResponse({ status: xhr.status } as Response, body));
    };
    xhr.onerror = () => reject(ApiError.network());
    xhr.onabort = () => reject(new DOMException('Завантаження скасовано', 'AbortError'));
    if (opts.signal) {
      if (opts.signal.aborted) {
        reject(new DOMException('Завантаження скасовано', 'AbortError'));
        return;
      }
      opts.signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }
    xhr.send(file);
  });
}

/** Вміст текстового файла (UTF-8). Знак порядку байтів (BOM) на початку прибирається. */
export async function readFileText(file: Blob): Promise<string> {
  const text = await file.text();
  return text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
}
